/**
 * Reloj del resultado clave (root/gestionsiaco#482).
 *
 * Mide cuanto tiempo pasa una persona DENTRO de un resultado clave para poder costear la operacion
 * por centro y subcentro. No es un componente ni vive en redux: es un modulo suelto, de modo que
 * medir no provoca un solo render de React ni una sola peticion al servidor.
 *
 * Como mide: comparando marcas de tiempo, nunca contando tics. Si entre dos lecturas hay un salto
 * grande (el equipo se suspendio, la pestana quedo en segundo plano) ese intervalo no se cuenta.
 * A los 15 minutos sin teclado, mouse ni scroll el reloj se frena, y se reanuda solo con el primer
 * movimiento.
 *
 * Como entrega el dato: no lo entrega. Lo acompana. El acumulado viaja en las cabeceras de los
 * guardados que la pantalla YA hace (apiSlice.prepareHeaders lo pone), asi que no hay peticion
 * nueva. La unica excepcion es la despedida, que recoge el rato final —el que va del ultimo guardado
 * al cierre— y solo sale si la sesion ya acompano algun guardado: quien solo pasa a mirar no deja
 * fila.
 */

/** Milisegundos sin actividad tras los cuales el reloj se frena */
const FRENO = 15 * 60 * 1000;
/** Cada cuanto se revisa si hubo actividad (revisar no es contar) */
const LATIDO = 5 * 1000;
/**
 * Salto entre dos lecturas que delata un equipo suspendido o una pestana congelada: con el latido
 * cada 5 segundos, un intervalo de mas de un minuto solo puede significar que el navegador dejo de
 * correr. Ese rato no se cuenta.
 *
 * Ojo con bajarlo: esto NO es el freno por inactividad. Mientras el reloj no haya frenado, el
 * tiempo cuenta aunque la persona no toque el teclado; leer un documento tambien es trabajar.
 */
const SALTO = 60 * 1000;
/** Llave del acumulado en el navegador */
const LLAVE = "cima.relojrc";

let oSesion = null;
let nLatido = null;

/** Lee el acumulado guardado, tolerando navegacion privada o almacenamiento bloqueado */
const leerGuardado = () => {
  try {
    return JSON.parse(window.localStorage.getItem(LLAVE) || "null");
  } catch (error) {
    return null;
  }
};

/** Guarda el acumulado; si el navegador no deja, la medicion sigue viva en memoria */
const guardar = () => {
  try {
    if (oSesion) {
      window.localStorage.setItem(LLAVE, JSON.stringify(oSesion));
    } else {
      window.localStorage.removeItem(LLAVE);
    }
  } catch (error) {
    // Sin almacenamiento la sesion vive solo en memoria: se pierde al refrescar, nada mas
  }
};

/** Identificador de la sesion de trabajo: hace idempotente el envio */
const nuevaLlave = () => {
  const aleatorio = Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}${aleatorio}`.toUpperCase().slice(0, 40);
};

/** Marca de apertura en el formato que entiende el servidor */
const marcaDe = (nMomento) => {
  const oFecha = new Date(nMomento);
  const dosDigitos = (nValor) => String(nValor).padStart(2, "0");
  return (
    `${oFecha.getFullYear()}-${dosDigitos(oFecha.getMonth() + 1)}-${dosDigitos(oFecha.getDate())} ` +
    `${dosDigitos(oFecha.getHours())}:${dosDigitos(oFecha.getMinutes())}:${dosDigitos(oFecha.getSeconds())}`
  );
};

/**
 * Suma lo transcurrido desde la ultima lectura.
 *
 * Mientras el reloj este vivo, el tiempo CUENTA aunque la persona no toque nada: leer, pensar o
 * revisar un documento tambien es trabajar. Solo deja de contar en dos casos: cuando el equipo
 * estuvo suspendido (salto enorme entre lecturas) y cuando ya pasaron los 15 minutos de freno.
 */
const acumular = () => {
  if (!oSesion) return;
  const nAhora = Date.now();
  const nDesde = nAhora - oSesion.nUltimaLectura;
  oSesion.nUltimaLectura = nAhora;
  if (nDesde <= 0) return;
  const bDormida = nDesde > SALTO;
  const bQuieta = nAhora - oSesion.nUltimaActividad > FRENO;
  //Pausada: la persona salió del resultado clave. Apagar el latido no basta, porque esta función
  //la vuelve a llamar CADA guardado de la aplicación —las cabeceras del reloj acompañan todas las
  //mutaciones—, así que el rato que alguien pasa trabajando en OTRA pantalla se le cargaba al
  //resultado clave que dejó abierto. Mientras esté pausada, el tiempo va a nPausado y nunca a
  //nSegundos.
  if (oSesion.bPausada || bDormida || bQuieta) {
    oSesion.nPausado += Math.round(nDesde / 1000);
    return;
  }
  oSesion.nSegundos += Math.round(nDesde / 1000);
};

/** Anota actividad humana: es lo que reanuda el reloj frenado */
const hubActividad = () => {
  if (!oSesion) return;
  acumular();
  oSesion.nUltimaActividad = Date.now();
};

const EVENTOS = ["mousedown", "keydown", "wheel", "touchstart", "scroll"];

/** Enciende el latido y la escucha de actividad */
const escuchar = () => {
  if (nLatido) return;
  EVENTOS.forEach((cEvento) =>
    window.addEventListener(cEvento, hubActividad, { passive: true, capture: true })
  );
  nLatido = window.setInterval(() => {
    acumular();
    guardar();
  }, LATIDO);
};

/** Apaga el latido y la escucha */
const callar = () => {
  if (!nLatido) return;
  EVENTOS.forEach((cEvento) => window.removeEventListener(cEvento, hubActividad, { capture: true }));
  window.clearInterval(nLatido);
  nLatido = null;
};

/**
 * Abre o reanuda la medicion de un resultado clave.
 * Si la persona vuelve al mismo resultado clave tras refrescar o salir del modulo,
 * se retoma el acumulado en vez de empezar de cero.
 *
 * @param {string|number} DOCNROXX Numero del DO
 * @param {string|number} RESCLAXX Resultado clave
 */
export const abrirReloj = (DOCNROXX, RESCLAXX, TIPO = "RC", LLAVE = "") => {
  //Un producto se clasifica sin operación: ahí no hay DO y lo que identifica la unidad es la llave
  if (TIPO === "PRODUCTO" ? !LLAVE : !DOCNROXX || !RESCLAXX) return;
  const oPrevia = oSesion || leerGuardado();
  const bMismo =
    oPrevia &&
    String(oPrevia.DOCNROXX) === String(DOCNROXX) &&
    String(oPrevia.RESCLAXX) === String(RESCLAXX) &&
    (oPrevia.TIPO ?? "RC") === TIPO &&
    String(oPrevia.LLAVE ?? "") === String(LLAVE);
  const nAhora = Date.now();
  oSesion = bMismo
    ? { ...oPrevia, bPausada: false, nUltimaLectura: nAhora, nUltimaActividad: nAhora }
    : {
        DOCNROXX: String(DOCNROXX ?? ""),
        RESCLAXX: String(RESCLAXX ?? ""),
        TIPO,
        LLAVE: String(LLAVE ?? ""),
        cLlave: nuevaLlave(),
        cInicio: marcaDe(nAhora),
        bPausada: false,
        //Todavía no ha acompañado ningún guardado: hasta que lo haga, esto es una consulta
        bViajo: false,
        nSegundos: 0,
        nPausado: 0,
        nUltimaLectura: nAhora,
        nUltimaActividad: nAhora,
      };
  guardar();
  escuchar();
};

/**
 * Suspende la medicion sin cerrar la sesion: la persona cerro el resultado clave pero
 * puede volver. El acumulado queda guardado y viaja con el proximo guardado.
 */
export const pausarReloj = () => {
  if (!oSesion) return;
  acumular();
  //La sesión queda marcada, no solo sin latido: lo que la reanuda es volver a abrir el resultado
  //clave, nunca un guardado hecho en otra pantalla
  oSesion.bPausada = true;
  guardar();
  callar();
};

/**
 * Cierra la sesion de trabajo. Se usa cuando el resultado clave quedo COMPLETADO.
 * El tiempo ya viajo dentro de ese guardado, asi que aqui solo se limpia.
 */
export const cerrarReloj = () => {
  oSesion = null;
  guardar();
  callar();
};

/** Segundos medidos hasta ahora, para pintarlos en pantalla */
export const segundosDelReloj = () => {
  if (!oSesion) return 0;
  acumular();
  return oSesion.nSegundos;
};

/**
 * Qué está midiendo ahora, o null si no hay sesión. Sirve para no cerrar por error la sesión de
 * OTRA unidad y para que la píldora sepa si el tiempo que muestra es el de esta pantalla.
 *
 * @returns {{DOCNROXX: string, RESCLAXX: string, TIPO: string, LLAVE: string}|null}
 */
export const relojEnCurso = () => {
  const oActual = oSesion || leerGuardado();
  if (!oActual) return null;
  return {
    DOCNROXX: oActual.DOCNROXX,
    RESCLAXX: oActual.RESCLAXX,
    TIPO: oActual.TIPO ?? "RC",
    LLAVE: oActual.LLAVE ?? "",
  };
};

/** Indica si el reloj no esta contando: por inactividad o porque se salio del resultado clave */
export const relojFrenado = () =>
  !!oSesion && (oSesion.bPausada === true || Date.now() - oSesion.nUltimaActividad > FRENO);

/**
 * Prefijos con los que se nombran las CONSULTAS. En CIMA hay consultas escritas como mutacion
 * porque mandan cuerpo (getCountDO, getFichas, canResolveTask, validateInvoiceRestrictions...), y
 * la pantalla las dispara sola al abrirse. Si el reloj viajara dentro de ellas, abrir un resultado
 * clave para mirarlo ya dejaria una sesion grabada: medido en local, abrir uno sin tocar nada
 * generaba siete "guardados".
 */
const CONSULTAS = /^(get|list|search|consult|validate|can|check|count|fetch|find|report|export|download|print|preview)/i;

/**
 * Indica si una mutacion es de verdad un guardado del usuario y no una consulta disfrazada.
 * Se decide por el nombre del endpoint: marcarlos uno a uno serian 71 slices.
 *
 * @param {string} cEndpoint Nombre del endpoint de RTK Query
 * @returns {boolean} true cuando el reloj debe acompanar esa peticion
 */
export const esGuardadoDeVerdad = (cEndpoint) => !CONSULTAS.test(String(cEndpoint ?? ""));

/**
 * Cabeceras del reloj para una peticion de escritura. Es lo unico que toca el flujo normal:
 * seis cabeceras dentro de un guardado que la pantalla ya hacia.
 *
 * @param {boolean} bCierra true cuando el guardado cierra el resultado clave
 * @param {boolean} bAcompanaGuardado false solo para la despedida, que no es un guardado de la pantalla
 * @returns {object} Cabeceras, vacio cuando no hay sesion abierta
 */
export const cabecerasDelReloj = (bCierra = false, bAcompanaGuardado = true) => {
  if (!oSesion) return {};
  acumular();
  //Pausada: la persona ya salio del resultado clave. Lo que guarde despues en OTRA pantalla no
  //tiene nada que ver con el, asi que el reloj no se cuelga de ese guardado. Si no, abrir un
  //resultado clave, mirarlo, salir y guardar cualquier cosa en otro lado dejaba grabado el rato de
  //la mirada: el tiempo se registraba sin haber guardado nunca el resultado clave. El acumulado
  //sigue vivo en el navegador y viaja cuando se vuelva a el y se guarde de verdad.
  if (bAcompanaGuardado && oSesion.bPausada) return {};
  //Queda constancia de que esta sesión viajó dentro de un guardado: es lo que distingue a quien
  //trabajó de quien solo pasó a mirar, y de eso depende que haya despedida al cerrar.
  if (bAcompanaGuardado && !oSesion.bViajo) {
    oSesion.bViajo = true;
    guardar();
  }
  const cTipo = oSesion.TIPO ?? "RC";
  return {
    Relojdoxx: oSesion.DOCNROXX,
    //El resultado clave va SOLO cuando lo que se trabaja es un resultado clave. En una tarea o un
    //producto no hay ninguno, y mandar su identificador aqui haria que un servidor que todavia no
    //conozca los tipos lo grabara como un resultado clave que no existe: la cabecera viaja vacia y
    //ese servidor descarta la sesion en vez de ensuciar el costeo. Asi el orden del despliegue
    //—primero el servidor o primero la pantalla— deja de importar.
    Relojrcxx: cTipo === "RC" ? oSesion.RESCLAXX : "",
    //Que se esta trabajando y su identificador: un resultado clave, una tarea o un producto
    Relojtipx: cTipo,
    Relojunix: oSesion.RESCLAXX,
    //Codificada: la llave la forma el codigo del producto, que lo pone el cliente y puede traer
    //una tilde o una ñ. Una cabecera con un caracter fuera de ISO-8859-1 hace que el navegador
    //rechace la peticion entera, y estas cabeceras viajan en TODOS los guardados de la pantalla:
    //un codigo con tilde dejaria al clasificador sin poder guardar. El servidor la decodifica.
    Relojllav: encodeURIComponent(oSesion.LLAVE ?? ""),
    Relojsesx: oSesion.cLlave,
    Relojsegx: String(oSesion.nSegundos),
    Relojparx: String(oSesion.nPausado),
    Relojinix: oSesion.cInicio,
    Relojfinx: bCierra ? "SI" : "NO",
  };
};

/**
 * Despedida: unico envio propio de la medicion. Solo ocurre cuando alguien cierra la
 * pantalla con una sesion abierta. keepalive hace que el navegador lo termine de mandar
 * aunque la pestana ya se haya ido; sendBeacon no sirve porque no admite el token.
 */
export const despedirReloj = () => {
  if (!oSesion) return;
  //Quien solo consultó no deja rastro: la despedida sale únicamente si esta sesión ya acompañó al
  //menos un guardado. Abrir un resultado clave para mirarlo y cerrarlo no crea ninguna fila, así que
  //el costo de la operación no carga el rato de los que pasaron a ver.
  if (!oSesion.bViajo) return;
  const mCabeceras = { ...cabecerasDelReloj(false, false), Relojorix: "DESPEDIDA" };
  try {
    //La base viene SIN barra final (…/api), así que la barra la pone esta función. Se le quitan las
    //que traiga por si algún ambiente la configura al revés: `…/apirelojRc` no existe y el envío se
    //perdía sin que nada lo dijera.
    const cBase = String(process.env.REACT_APP_CIMA_ENDPOINT ?? "").replace(/\/+$/, "");
    window.fetch(`${cBase}/relojRc`, {
      method: "POST",
      keepalive: true,
      credentials: "include",
      //Accept obligatorio: el API es JSONAPI y sin esta cabecera responde 406 y descarta el envío.
      //Las demás peticiones la llevan porque las arma RTK Query; esta es la única a mano.
      headers: { Accept: "application/json", ...mCabeceras, ...tokenDeLaSesion() },
    });
  } catch (error) {
    // Si no alcanza a salir, el acumulado sigue en el navegador y viaja al reabrir el RC
  }
};

/**
 * Token guardado por redux-persist, leido sin importar el store (evita dependencia circular).
 * Vive en `persist:auth`, que es el único trozo del store que se persiste; se miran las dos llaves
 * por si algún día cambia la partición.
 */
const tokenDeLaSesion = () => {
  for (const cLlave of ["persist:auth", "persist:root"]) {
    try {
      const oPersistido = JSON.parse(window.localStorage.getItem(cLlave) || "{}");
      const cToken = JSON.parse(oPersistido.token || '""');
      if (cToken) return { Authorization: `Bearer ${cToken}` };
    } catch (error) {
      // Llave ausente o ilegible: se prueba la siguiente
    }
  }
  return {};
};
